import React, { useState, useRef, useEffect } from 'react';
import { Card, Row, Col, Modal, Button, Collapse, Form, Spinner } from 'react-bootstrap';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { uploadFile } from '../utils/firebase';
import { fetchPageMetadata } from '../utils/fetchProductImage';
import { trackPageView } from '../utils/analytics';
import MoodBoardGroup from './MoodBoardGroup';
import FloorPlanTab from './floorplan/FloorPlanTab';
import SocialShareDialog from './SocialShareDialog';

function DesignBoardPanel({ spaceAnalysis = [], messages = [], workItems = [], moodBoard = [], moodBoardGroups = [], onMoodBoardUpdate, uid, sessionId, sessionTitle, floorPlanData, onFloorPlanUpdate, decisionItems = [], onDecisionItemsChange, onWorkItemsChange, isAdmin = false, isDevMode = false, isActive = true }) {
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [hiddenImages, setHiddenImages] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [designSubTab, setDesignSubTab] = useState('designboard');
  const [shareImage, setShareImage] = useState(null);

  useEffect(() => {
    if (!isActive) return;
    trackPageView(`${window.location.pathname.replace(/\/$/, '')}/${designSubTab}`);
  }, [designSubTab, isActive]);

  // Mood board state
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', sourceUrl: '', notes: '', category: '', groupId: null, imageUrl: '', imageFile: null });
  const [addForm, setAddForm] = useState({ name: '', sourceUrl: '', groupId: '', imageFile: null });
  const addProductImageRef = useRef(null);

  // Group management state
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');

  // DnD state
  const [activeDragId, setActiveDragId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Generate a label for an image based on its category and metadata
  const generateLabel = (image, category) => {
    const roomType = image.room_type ? image.room_type.charAt(0).toUpperCase() + image.room_type.slice(1) : '';
    const style = image.design_style ? image.design_style.charAt(0).toUpperCase() + image.design_style.slice(1) : '';

    switch (category) {
      case 'current':
        return roomType ? `${roomType} - Current` : 'Current State';
      case 'inspiration':
        return roomType ? `${roomType} - Inspiration` : 'Inspiration';
      case 'product':
        if (image.product_details?.product_type) {
          const productType = image.product_details.product_type.charAt(0).toUpperCase() + image.product_details.product_type.slice(1);
          return image.product_details.brand ? `${image.product_details.brand} ${productType}` : productType;
        }
        return roomType ? `${roomType} - Product` : 'Product / Material';
      case 'generated': {
        const designPrefix = image.design_number ? `Design #${image.design_number}` : null;
        if (image.title) return designPrefix ? `${designPrefix}: ${image.title}` : image.title;
        if (image.changes_description) return designPrefix ? `${designPrefix}: ${image.changes_description}` : image.changes_description;
        if (designPrefix) return designPrefix;
        if (style && roomType) return `${roomType} - ${style}`;
        if (roomType) return `${roomType} - AI Design`;
        return 'AI Generated Design';
      }
      case 'uncategorized':
        return image.name || 'Uploaded Image';
      default:
        return 'Image';
    }
  };

  // Look up work item titles for generated design links
  const getWorkItemTitles = (workItemIds) => {
    if (!workItemIds || workItemIds.length === 0 || !workItems) return [];
    return workItemIds.map(id => {
      const item = workItems.find(w => w.id === id);
      return item ? item.title : null;
    }).filter(Boolean);
  };

  // Toggle image visibility
  const toggleHideImage = (imageId, e) => {
    e.stopPropagation();
    setHiddenImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  // === Mood Board handlers ===

  const handleAddProduct = async () => {
    setIsUploading(true);
    try {
      let fileUrl = '';
      let fileName = '';
      let fileType = '';

      if (addForm.imageFile && uid && sessionId) {
        const result = await uploadFile(uid, sessionId, addForm.imageFile);
        fileUrl = result.url;
        fileName = result.name;
        fileType = result.type;
      }

      const newItem = {
        id: `mb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 'product',
        name: addForm.name.trim(),
        fileUrl,
        fileName,
        fileType,
        sourceUrl: addForm.sourceUrl.trim(),
        groupId: addForm.groupId || null,
        createdAt: Date.now(),
      };

      // Fetch OG metadata if no image was uploaded and a source URL exists
      if (!fileUrl && newItem.sourceUrl) {
        const metadata = await fetchPageMetadata(newItem.sourceUrl);
        newItem.ogImageUrl = metadata.imageUrl;
        if (!newItem.name && metadata.title) {
          newItem.name = metadata.title;
        }
      }

      onMoodBoardUpdate?.([...moodBoard, newItem], moodBoardGroups);
      setAddForm({ name: '', sourceUrl: '', groupId: '', imageFile: null });
      setShowAddProduct(false);
    } catch (error) {
      console.error('Failed to add product:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteMoodBoardItem = (itemId) => {
    onMoodBoardUpdate?.(moodBoard.filter(item => item.id !== itemId), moodBoardGroups);
  };

  const handleStartEdit = (item) => {
    setEditingItemId(item.id);
    setEditForm({
      name: item.name || '',
      sourceUrl: item.sourceUrl || '',
      notes: item.notes || '',
      category: item.category || '',
      groupId: item.groupId || null,
      imageUrl: item.fileUrl ? '' : (item.ogImageUrl || ''),
      imageFile: null,
    });
  };

  const handleSaveEdit = async () => {
    const originalItem = moodBoard.find(item => item.id === editingItemId);
    const urlChanged = originalItem && editForm.sourceUrl !== originalItem.sourceUrl;

    // Handle image file upload
    let newFileUrl = '';
    let newFileName = '';
    let newFileType = '';
    if (editForm.imageFile && uid && sessionId) {
      const result = await uploadFile(uid, sessionId, editForm.imageFile);
      newFileUrl = result.url;
      newFileName = result.name;
      newFileType = result.type;
    }

    // Build image-related updates
    const imageUpdates = {};
    if (newFileUrl) {
      // New file uploaded — use it, clear ogImageUrl
      imageUpdates.fileUrl = newFileUrl;
      imageUpdates.fileName = newFileName;
      imageUpdates.fileType = newFileType;
      imageUpdates.ogImageUrl = '';
    } else if (editForm.imageUrl !== (originalItem.fileUrl ? '' : (originalItem.ogImageUrl || ''))) {
      // Image URL changed manually — use it, clear fileUrl
      imageUpdates.ogImageUrl = editForm.imageUrl;
      imageUpdates.fileUrl = '';
      imageUpdates.fileName = '';
      imageUpdates.fileType = '';
    } else if (urlChanged && !originalItem.fileUrl) {
      // Source URL changed and no uploaded file — will re-fetch OG below
      imageUpdates.ogImageUrl = null;
    }

    const updates = moodBoard.map(item =>
      item.id === editingItemId
        ? { ...item, name: editForm.name, sourceUrl: editForm.sourceUrl, notes: editForm.notes, category: editForm.category, groupId: editForm.groupId, ...imageUpdates }
        : item
    );
    onMoodBoardUpdate?.(updates, moodBoardGroups);
    setEditingItemId(null);

    // Re-fetch OG metadata if URL changed, no uploaded file, and no manual image URL
    if (urlChanged && !newFileUrl && !editForm.imageUrl && editForm.sourceUrl) {
      const metadata = await fetchPageMetadata(editForm.sourceUrl);
      if (metadata.imageUrl) {
        onMoodBoardUpdate?.(updates.map(item =>
          item.id === editingItemId ? { ...item, ogImageUrl: metadata.imageUrl } : item
        ), moodBoardGroups);
      }
    }
  };

  // === Group CRUD handlers ===

  const handleCreateGroup = () => {
    if (!newGroupName.trim()) return;
    const existing = moodBoardGroups.find(
      g => g.name.toLowerCase() === newGroupName.trim().toLowerCase()
    );
    if (existing) {
      setNewGroupName('');
      setShowAddGroup(false);
      return;
    }
    const newGroup = {
      id: `mbg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: newGroupName.trim(),
      order: moodBoardGroups.length,
      createdAt: Date.now(),
    };
    onMoodBoardUpdate?.(moodBoard, [...moodBoardGroups, newGroup]);
    setNewGroupName('');
    setShowAddGroup(false);
  };

  const handleRenameGroup = (groupId, newName) => {
    const updatedGroups = moodBoardGroups.map(g =>
      g.id === groupId ? { ...g, name: newName } : g
    );
    onMoodBoardUpdate?.(moodBoard, updatedGroups);
  };

  const handleDeleteGroup = (groupId) => {
    const updatedGroups = moodBoardGroups.filter(g => g.id !== groupId);
    const updatedItems = moodBoard.map(item =>
      item.groupId === groupId ? { ...item, groupId: null } : item
    );
    onMoodBoardUpdate?.(updatedItems, updatedGroups);
  };

  // === Drag and Drop handlers ===

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragOver = () => {
    // Visual feedback handled by CSS via isOver state in MoodBoardGroup
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!over) return;

    const activeItemId = active.id;
    const overId = over.id;

    // Determine if we dropped onto a group container or another item
    const isOverGroup = overId === 'ungrouped' || moodBoardGroups.some(g => g.id === overId);
    const isOverItem = moodBoard.some(i => i.id === overId);

    let targetGroupId = null;

    if (isOverGroup) {
      targetGroupId = overId === 'ungrouped' ? null : overId;
    } else if (isOverItem) {
      const overItem = moodBoard.find(i => i.id === overId);
      targetGroupId = overItem?.groupId || null;
    } else {
      return;
    }

    const activeItem = moodBoard.find(i => i.id === activeItemId);
    if (!activeItem) return;

    const sourceGroupId = activeItem.groupId || null;

    if (sourceGroupId === targetGroupId) {
      // Reorder within same group
      if (isOverItem && activeItemId !== overId) {
        const groupItems = moodBoard.filter(i => (i.groupId || null) === sourceGroupId);
        const oldIndex = groupItems.findIndex(i => i.id === activeItemId);
        const newIndex = groupItems.findIndex(i => i.id === overId);
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(groupItems, oldIndex, newIndex);
          // Rebuild the full array preserving other groups' items
          const otherItems = moodBoard.filter(i => (i.groupId || null) !== sourceGroupId);
          // Insert reordered items at the position of the first original item in this group
          const firstOrigIdx = moodBoard.findIndex(i => (i.groupId || null) === sourceGroupId);
          const newMoodBoard = [
            ...otherItems.slice(0, firstOrigIdx >= 0 ? firstOrigIdx : otherItems.length),
            ...reordered,
            ...otherItems.slice(firstOrigIdx >= 0 ? firstOrigIdx : otherItems.length),
          ];
          onMoodBoardUpdate?.(newMoodBoard, moodBoardGroups);
        }
      }
    } else {
      // Move to different group
      const updatedItems = moodBoard.map(item =>
        item.id === activeItemId ? { ...item, groupId: targetGroupId } : item
      );
      onMoodBoardUpdate?.(updatedItems, moodBoardGroups);
    }
  };

  const activeDragItem = activeDragId ? moodBoard.find(i => i.id === activeDragId) : null;

  // Group items by groupId
  const sortedGroups = [...moodBoardGroups].sort((a, b) => (a.order || 0) - (b.order || 0));
  const ungroupedItems = moodBoard.filter(item => !item.groupId || !moodBoardGroups.some(g => g.id === item.groupId));
  const getGroupItems = (groupId) => moodBoard.filter(item => item.groupId === groupId);

  // === Design Board image processing ===

  const currentStateImages = [];
  const inspirationImages = [];
  const productImages = [];
  const generatedDesigns = [];

  // Track design numbering — use stored design_number if available, otherwise assign by order
  let designCounter = 0;
  spaceAnalysis.forEach((analysis, analysisIdx) => {
    if (analysis.analysis_type === 'generated_design' && (analysis.image_url || analysis.image_data)) {
      designCounter++;
      const designNumber = analysis.design_number || designCounter;
      generatedDesigns.push({
        id: `generated-${analysis.id || analysisIdx}`,
        url: analysis.image_url || `data:${analysis.mime_type || 'image/png'};base64,${analysis.image_data}`,
        title: analysis.title || null,
        design_number: designNumber,
        summary: analysis.prompt,
        room_type: analysis.room_type,
        design_style: analysis.design_style,
        analysis_id: analysis.id,
        isGenerated: true,
        category: 'generated',
        work_item_ids: analysis.work_item_ids || [],
        changes_description: analysis.changes_description || '',
      });
      return;
    }

    let category;
    if (analysis.analysis_type === 'current_state') category = 'current';
    else if (analysis.analysis_type === 'product') category = 'product';
    else category = 'inspiration';

    const images = (analysis.image_urls || []).map((url, urlIdx) => ({
      id: `${analysis.analysis_type}-${analysis.id || analysisIdx}-${urlIdx}`,
      url,
      summary: analysis.summary,
      room_type: analysis.room_type,
      style_assessment: analysis.style_assessment,
      product_details: analysis.product_details || null,
      analysis_id: analysis.id,
      category,
    }));

    if (category === 'current') currentStateImages.push(...images);
    else if (category === 'product') productImages.push(...images);
    else inspirationImages.push(...images);
  });

  const categorizedUrls = new Set([
    ...currentStateImages.map(i => i.url),
    ...inspirationImages.map(i => i.url),
    ...productImages.map(i => i.url),
  ]);

  const uncategorizedImages = [];
  messages.forEach((msg, msgIdx) => {
    if (msg.images) {
      const imgs = typeof msg.images === 'string' ? JSON.parse(msg.images) : msg.images;
      imgs.forEach((img, imgIdx) => {
        if (!categorizedUrls.has(img.url) && (!img.type || img.type.startsWith('image/'))) {
          uncategorizedImages.push({
            id: `uncategorized-${msgIdx}-${imgIdx}`,
            url: img.url,
            name: img.name,
            category: 'uncategorized',
          });
        }
      });
    }
  });

  const visibleCurrentState = currentStateImages.filter(img => !hiddenImages.has(img.id));
  const visibleInspiration = inspirationImages.filter(img => !hiddenImages.has(img.id));
  const visibleProduct = productImages.filter(img => !hiddenImages.has(img.id));
  const visibleGenerated = generatedDesigns.filter(img => !hiddenImages.has(img.id));
  const visibleUncategorized = uncategorizedImages.filter(img => !hiddenImages.has(img.id));

  const allHiddenImages = [
    ...currentStateImages.filter(img => hiddenImages.has(img.id)),
    ...inspirationImages.filter(img => hiddenImages.has(img.id)),
    ...productImages.filter(img => hiddenImages.has(img.id)),
    ...generatedDesigns.filter(img => hiddenImages.has(img.id)),
    ...uncategorizedImages.filter(img => hiddenImages.has(img.id)),
  ];

  const hasAnyImages = currentStateImages.length > 0 || inspirationImages.length > 0 || productImages.length > 0 || generatedDesigns.length > 0 || uncategorizedImages.length > 0;
  const hasVisibleImages = visibleCurrentState.length > 0 || visibleInspiration.length > 0 || visibleProduct.length > 0 || visibleGenerated.length > 0 || visibleUncategorized.length > 0;

  const ImageCard = ({ image, isHidden = false }) => {
    const label = generateLabel(image, image.category);
    const linkedWorkItems = getWorkItemTitles(image.work_item_ids);
    return (
      <Col xs={12} md={4} lg={3} className="mb-3">
        <Card className={`design-board-card h-100 ${isHidden ? 'hidden-image-card' : ''}`}>
          <div className="design-board-image-wrapper" onClick={() => setFullscreenImage(image)}>
            <Card.Img
              variant="top"
              src={image.url}
              alt={label}
              className="design-board-image"
            />
            <Button
              variant="light"
              size="sm"
              className="image-share-btn"
              onClick={(e) => { e.stopPropagation(); setShareImage(image); }}
              title="Share image"
            >
              <i className="bi bi-share"></i>
            </Button>
            <Button
              variant="light"
              size="sm"
              className="image-hide-btn"
              onClick={(e) => toggleHideImage(image.id, e)}
              title={isHidden ? 'Show image' : 'Hide image'}
            >
              <i className={`bi ${isHidden ? 'bi-eye' : 'bi-eye-slash'}`}></i>
            </Button>
          </div>
          <Card.Body className="p-2 design-board-label">
            <small className="text-muted">{label}</small>
            {image.product_details && (
              <div className="product-detail-badges mt-1">
                {image.product_details.material && (
                  <span className="badge bg-light text-dark me-1">{image.product_details.material}</span>
                )}
                {image.product_details.color && (
                  <span className="badge bg-light text-dark me-1">{image.product_details.color}</span>
                )}
                {image.product_details.pattern && (
                  <span className="badge bg-light text-dark me-1">{image.product_details.pattern}</span>
                )}
              </div>
            )}
            {linkedWorkItems.length > 0 && (
              <div className="work-item-links mt-1">
                {linkedWorkItems.map((title, i) => (
                  <small key={i} className="d-block text-primary">
                    <i className="bi bi-link-45deg me-1"></i>{title}
                  </small>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    );
  };

  // === Render ===

  return (
    <div className="design-board-panel d-flex flex-column h-100">
      <div className="design-board-header p-3 border-bottom bg-white">
        <h5 className="mb-2">
          <i className="bi bi-palette me-2"></i>
          Design
        </h5>
        {/* Sub-tabs */}
        <div className="design-sub-tabs d-flex gap-1">
          <button
            className={`design-sub-tab ${designSubTab === 'moodboard' ? 'active' : ''}`}
            onClick={() => setDesignSubTab('moodboard')}
          >
            <i className="bi bi-heart me-1"></i>
            Mood Board
            {moodBoard.length > 0 && <span className="badge bg-secondary ms-1">{moodBoard.length}</span>}
          </button>
          <button
            className={`design-sub-tab ${designSubTab === 'designboard' ? 'active' : ''}`}
            onClick={() => setDesignSubTab('designboard')}
          >
            <i className="bi bi-images me-1"></i>
            Design Board
          </button>
          <button
            className={`design-sub-tab ${designSubTab === 'floorplan' ? 'active' : ''}`}
            onClick={() => setDesignSubTab('floorplan')}
          >
            <i className="bi bi-bounding-box me-1"></i>
            Floor Plan
          </button>
        </div>
      </div>

      {/* Floor Plan Tab - rendered outside the scrollable content area */}
      {designSubTab === 'floorplan' && (
        <div className="floor-plan-content flex-grow-1 overflow-hidden">
          <FloorPlanTab
            floorPlanData={floorPlanData}
            onFloorPlanUpdate={onFloorPlanUpdate}
          />
        </div>
      )}

      <div className={`design-board-content flex-grow-1 overflow-auto p-3 ${designSubTab === 'floorplan' ? 'd-none' : ''}`}>
        {/* === Mood Board Tab === */}
        {designSubTab === 'moodboard' && (
          <div className="mood-board-tab">
            {/* Add Card and New Group buttons */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAddProduct(!showAddProduct)}
                disabled={isUploading}
              >
                <i className="bi bi-plus-lg me-1"></i>Add Card
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAddGroup(!showAddGroup)}
              >
                <i className="bi bi-folder-plus me-1"></i>New Group
              </Button>
            </div>

            {/* Add Group Form */}
            <Collapse in={showAddGroup}>
              <div>
                <Card className="mb-3 border-secondary">
                  <Card.Body className="p-3">
                    <h6 className="mb-2">Create Group</h6>
                    <div className="d-flex gap-2">
                      <Form.Control
                        size="sm"
                        placeholder="Group name (e.g., Kitchen, Master Bathroom)"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleCreateGroup();
                          if (e.key === 'Escape') { setShowAddGroup(false); setNewGroupName(''); }
                        }}
                        autoFocus
                      />
                      <Button size="sm" variant="primary" onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
                        Create
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => { setShowAddGroup(false); setNewGroupName(''); }}>
                        Cancel
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>

            {/* Add Card Form */}
            <Collapse in={showAddProduct}>
              <div>
                <Card className="mb-3 border-primary">
                  <Card.Body className="p-3">
                    <h6 className="mb-3">Add Card</h6>
                    <Row className="g-2">
                      <Col xs={12} md={6}>
                        <Form.Control
                          size="sm"
                          placeholder="Product name"
                          value={addForm.name}
                          onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Control
                          size="sm"
                          placeholder="Product URL"
                          value={addForm.sourceUrl}
                          onChange={(e) => setAddForm({ ...addForm, sourceUrl: e.target.value })}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Select
                          size="sm"
                          value={addForm.groupId}
                          onChange={(e) => setAddForm({ ...addForm, groupId: e.target.value || '' })}
                        >
                          <option value="">Group...</option>
                          {moodBoardGroups.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col xs={12} md={6}>
                        <input
                          type="file"
                          ref={addProductImageRef}
                          onChange={(e) => {
                            if (e.target.files[0]) setAddForm({ ...addForm, imageFile: e.target.files[0] });
                          }}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="w-100"
                          onClick={() => addProductImageRef.current?.click()}
                        >
                          <i className="bi bi-image me-1"></i>
                          {addForm.imageFile ? addForm.imageFile.name : 'Add Image'}
                        </Button>
                      </Col>
                    </Row>
                    <div className="d-flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={handleAddProduct}
                        disabled={isUploading}
                      >
                        {isUploading ? <Spinner size="sm" /> : 'Add'}
                      </Button>
                      <Button size="sm" variant="outline-secondary" onClick={() => {
                        setShowAddProduct(false);
                        setAddForm({ name: '', sourceUrl: '', groupId: '', imageFile: null });
                      }}>
                        Cancel
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>

            {/* Mood Board Items - Grouped with DnD */}
            {moodBoard.length === 0 && moodBoardGroups.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-heart display-4 mb-3 d-block"></i>
                <p>No mood board items yet.</p>
                <p className="small">
                  Share inspiration images, PDFs, or product links with the AI in the chat and it will add them here automatically.
                  <br />
                  You can also add cards manually using the button above.
                </p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                {/* Named groups */}
                {sortedGroups.map(group => (
                  <MoodBoardGroup
                    key={group.id}
                    group={group}
                    items={getGroupItems(group.id)}
                    allGroups={moodBoardGroups}
                    editingItemId={editingItemId}
                    editForm={editForm}
                    onEditFormChange={setEditForm}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={() => setEditingItemId(null)}
                    onDeleteItem={handleDeleteMoodBoardItem}
                    onImageClick={setFullscreenImage}
                    onRenameGroup={handleRenameGroup}
                    onDeleteGroup={handleDeleteGroup}
                    onAddCard={() => {
                      setAddForm({ name: '', sourceUrl: '', groupId: group.id, imageFile: null });
                      setShowAddProduct(true);
                    }}
                  />
                ))}

                {/* Ungrouped items */}
                {(ungroupedItems.length > 0 || moodBoardGroups.length > 0) && (
                  <MoodBoardGroup
                    group={null}
                    items={ungroupedItems}
                    allGroups={moodBoardGroups}
                    isUngrouped={true}
                    editingItemId={editingItemId}
                    editForm={editForm}
                    onEditFormChange={setEditForm}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={() => setEditingItemId(null)}
                    onDeleteItem={handleDeleteMoodBoardItem}
                    onImageClick={setFullscreenImage}
                    onRenameGroup={() => {}}
                    onDeleteGroup={() => {}}
                    onAddCard={() => {
                      setAddForm({ name: '', sourceUrl: '', groupId: '', imageFile: null });
                      setShowAddProduct(true);
                    }}
                  />
                )}

                {/* Drag Overlay */}
                <DragOverlay>
                  {activeDragItem ? (
                    <div className="mood-board-drag-overlay">
                      <Card className="mood-board-card" style={{ width: 280, opacity: 0.9 }}>
                        <Card.Body className="p-2">
                          <div className="fw-medium small text-truncate">{activeDragItem.name}</div>
                          {activeDragItem.category && (
                            <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>
                              {activeDragItem.category}
                            </span>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        )}

        {/* === Design Board Tab === */}
        {designSubTab === 'designboard' && (
          <div className="design-board-tab">
            {!hasAnyImages ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-images display-4 mb-3 d-block"></i>
                <p>No images yet.</p>
                <p className="small">
                  Upload photos of your current space and inspiration images in the chat.
                  <br />
                  The AI will organize them here automatically.
                </p>
              </div>
            ) : (
              <>
                {!hasVisibleImages && allHiddenImages.length > 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-eye-slash display-4 mb-3 d-block"></i>
                    <p>All images are hidden.</p>
                    <p className="small">Expand the hidden images section below to see them.</p>
                  </div>
                ) : (
                  <>
                    {visibleCurrentState.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">
                          <i className="bi bi-house me-2"></i>Current State
                        </h6>
                        <Row>
                          {visibleCurrentState.map((image) => (
                            <ImageCard key={image.id} image={image} />
                          ))}
                        </Row>
                      </div>
                    )}

                    {visibleInspiration.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">
                          <i className="bi bi-stars me-2"></i>Inspiration
                        </h6>
                        <Row>
                          {visibleInspiration.map((image) => (
                            <ImageCard key={image.id} image={image} />
                          ))}
                        </Row>
                      </div>
                    )}

                    {visibleProduct.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">
                          <i className="bi bi-box-seam me-2"></i>Products & Materials
                        </h6>
                        <Row>
                          {visibleProduct.map((image) => (
                            <ImageCard key={image.id} image={image} />
                          ))}
                        </Row>
                      </div>
                    )}

                    {visibleGenerated.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">
                          <i className="bi bi-magic me-2"></i>AI-Generated Designs
                        </h6>
                        <Row>
                          {visibleGenerated.map((image) => (
                            <ImageCard key={image.id} image={image} />
                          ))}
                        </Row>
                      </div>
                    )}

                    {visibleUncategorized.length > 0 && (
                      <div className="mb-4">
                        <h6 className="text-muted mb-3">
                          <i className="bi bi-image me-2"></i>Uploaded Images
                        </h6>
                        <Row>
                          {visibleUncategorized.map((image) => (
                            <ImageCard key={image.id} image={image} />
                          ))}
                        </Row>
                      </div>
                    )}

                    <div className="design-board-tip p-3 bg-light rounded mt-4">
                      <p className="mb-2 small">
                        <i className="bi bi-lightbulb me-2 text-warning"></i>
                        <strong>Tip:</strong> Upload product images (tiles, fixtures, paint swatches) and label them as "Product" to see how they look in your space.
                      </p>
                      <p className="mb-0 small">
                        Try saying: <em>"Use the tile from this image in our bathroom design"</em> or <em>"Show me what the bathroom would look like with just the new vanity."</em>
                      </p>
                    </div>
                  </>
                )}

                {allHiddenImages.length > 0 && (
                  <div className="hidden-images-section mt-4">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowHidden(!showHidden)}
                      className="w-100 d-flex align-items-center justify-content-between"
                    >
                      <span>
                        <i className="bi bi-eye-slash me-2"></i>
                        Hidden Images ({allHiddenImages.length})
                      </span>
                      <i className={`bi bi-chevron-${showHidden ? 'up' : 'down'}`}></i>
                    </Button>
                    <Collapse in={showHidden}>
                      <div className="hidden-images-content mt-3">
                        <Row>
                          {allHiddenImages.map((image) => (
                            <ImageCard key={image.id} image={image} isHidden={true} />
                          ))}
                        </Row>
                      </div>
                    </Collapse>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      <Modal
        show={!!fullscreenImage}
        onHide={() => setFullscreenImage(null)}
        centered
        size="xl"
        className="fullscreen-image-modal"
      >
        <Modal.Header closeButton className="border-0 position-absolute top-0 end-0" style={{ zIndex: 1 }}>
        </Modal.Header>
        <Modal.Body className="p-0 d-flex align-items-center justify-content-center bg-dark">
          {fullscreenImage && (
            <img
              src={fullscreenImage.url}
              alt={fullscreenImage.summary || fullscreenImage.name || 'Full size image'}
              className="fullscreen-modal-image"
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        {fullscreenImage && (
          <Modal.Footer className="bg-dark text-white border-0 justify-content-between">
            <div className="text-center flex-grow-1">
              {(fullscreenImage.title || fullscreenImage.design_number || fullscreenImage.room_type) && (
                <small className="text-uppercase d-block text-muted">
                  {fullscreenImage.design_number && fullscreenImage.title
                    ? `Design #${fullscreenImage.design_number}: ${fullscreenImage.title}`
                    : fullscreenImage.title || (fullscreenImage.design_number ? `Design #${fullscreenImage.design_number}` : fullscreenImage.room_type)}
                </small>
              )}
            </div>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => { setShareImage(fullscreenImage); setFullscreenImage(null); }}
            >
              <i className="bi bi-share me-1"></i>Share
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Social Share Dialog */}
      <SocialShareDialog
        show={!!shareImage}
        onHide={() => setShareImage(null)}
        image={shareImage}
        currentStateImages={currentStateImages}
        sessionTitle={sessionTitle}
        uid={uid}
        sessionId={sessionId}
      />
    </div>
  );
}

export default DesignBoardPanel;
